import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery, Paper } from "@mui/material";
import { motion } from "framer-motion";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getMissionVesion } from "../services/api/getAll";
import { useTranslation } from "react-i18next";

const MissionVision = () => {
  const ref = useRef(null);
  const { t, i18n } = useTranslation();
  const [mission, setMission] = useState("");
  const [vision, setVision] = useState("");
  const preferredLanguage = localStorage.getItem("language") || "en";
  const isArabic = preferredLanguage === "ar";
  const isMobile = useMediaQuery("(max-width: 900px)");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const preferredLanguage = localStorage.getItem("language") || "en";
        const response = await getMissionVesion();
        const enTranslation = response.items?.[0]?.translations?.find(
          (t: any) => t.languageCode === preferredLanguage
        );

        if (enTranslation) {
          setMission(enTranslation.mission || "");
          setVision(enTranslation.vision || "");
        }
      } catch (error) {
        console.error("Failed to load mission & vision:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <Box
      ref={ref}
      id="mission-vision"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 8, md: 16 },
        px: { xs: 2, md: 3 },
        background: "transparent",
        zIndex: 1,
        textAlign: "center",
      }}
    >
      <Box
        key={`${mission ? 1 : 0}-${i18n.language}`}
        sx={{ position: "relative", zIndex: 2 }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            fontWeight={900}
            mb={2}
            sx={{
              fontFamily: "Poppins, Ozen",
              background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textTransform: "uppercase",
              letterSpacing: 1,
              textShadow: "0 0 30px rgba(255, 174, 0, 0.1)",
            }}
          >
            {t("ourMissionAndVision")}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.85 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <Typography
            variant="body1"
            maxWidth="700px"
            mx="auto"
            mb={8}
            dir={isArabic ? "rtl" : "ltr"}
            sx={{
              color: "#ccc",
              fontFamily: "Poppins",
              lineHeight: 1.8,
              fontSize: "1.1rem",
              textAlign: "center",
            }}
          >
            {t("missionDescription")}
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "stretch",
            gap: 4,
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          {/* 🎯 Mission Card */}
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : -60, y: isMobile ? 40 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] as const }}
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 5, md: 6 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.02)",
                backdropFilter: "blur(20px)",
                borderRadius: "32px",
                border: "1px solid rgba(255, 174, 0, 0.12)",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
                direction: isArabic ? "rtl" : "ltr",
                transition: "all 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
                "&:hover": {
                  transform: "translateY(-10px)",
                  border: "1px solid rgba(255, 174, 0, 0.35)",
                  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8), 0 0 35px rgba(255, 174, 0, 0.15)",
                  "& .card-glow-bg": {
                    opacity: 0.15,
                    transform: "scale(1.2)",
                  },
                  "& .card-icon-container": {
                    background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
                    color: "#000",
                    boxShadow: "0 0 25px rgba(255, 174, 0, 0.4)",
                    transform: "rotate(360deg)",
                  }
                }
              }}
            >
              {/* Artistic Blurred Orb Background */}
              <Box
                className="card-glow-bg"
                sx={{
                  position: "absolute",
                  top: "-50px",
                  right: isArabic ? "auto" : "-50px",
                  left: isArabic ? "-50px" : "auto",
                  width: "180px",
                  height: "180px",
                  background: "radial-gradient(circle, rgba(255, 174, 0, 0.3) 0%, rgba(255, 174, 0, 0) 70%)",
                  filter: "blur(20px)",
                  opacity: 0.08,
                  zIndex: 0,
                  transition: "all 0.5s ease",
                  pointerEvents: "none",
                }}
              />

              {/* Header: Icon + Title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  mb: 3,
                  zIndex: 1,
                }}
              >
                <Box
                  className="card-icon-container"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "56px",
                    height: "56px",
                    borderRadius: "16px",
                    background: "rgba(255, 174, 0, 0.08)",
                    border: "1px solid rgba(255, 174, 0, 0.25)",
                    color: "#FFAE00",
                    transition: "all 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
                  }}
                >
                  <TrackChangesIcon sx={{ fontSize: "28px" }} />
                </Box>

                <Typography
                  variant="h4"
                  fontWeight={900}
                  sx={{
                    color: "#FFAE00",
                    fontFamily: "Poppins",
                    fontSize: { xs: "1.45rem", md: "1.85rem" },
                  }}
                >
                  {t("ourMission")}
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: "#e0e0e0",
                  fontFamily: "Poppins",
                  lineHeight: 1.85,
                  textAlign: "justify",
                  fontSize: "1.05rem",
                  zIndex: 1,
                }}
              >
                {mission || ""}
              </Typography>
            </Paper>
          </motion.div>

          {/* 👁️ Vision Card */}
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : 60, y: isMobile ? 40 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] as const }}
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 5, md: 6 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.02)",
                backdropFilter: "blur(20px)",
                borderRadius: "32px",
                border: "1px solid rgba(255, 174, 0, 0.12)",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
                direction: isArabic ? "rtl" : "ltr",
                transition: "all 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
                "&:hover": {
                  transform: "translateY(-10px)",
                  border: "1px solid rgba(255, 174, 0, 0.35)",
                  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8), 0 0 35px rgba(255, 174, 0, 0.15)",
                  "& .card-glow-bg": {
                    opacity: 0.15,
                    transform: "scale(1.2)",
                  },
                  "& .card-icon-container": {
                    background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
                    color: "#000",
                    boxShadow: "0 0 25px rgba(255, 174, 0, 0.4)",
                    transform: "rotate(360deg)",
                  }
                }
              }}
            >
              {/* Artistic Blurred Orb Background */}
              <Box
                className="card-glow-bg"
                sx={{
                  position: "absolute",
                  top: "-50px",
                  right: isArabic ? "auto" : "-50px",
                  left: isArabic ? "-50px" : "auto",
                  width: "180px",
                  height: "180px",
                  background: "radial-gradient(circle, rgba(255, 174, 0, 0.3) 0%, rgba(255, 174, 0, 0) 70%)",
                  filter: "blur(20px)",
                  opacity: 0.08,
                  zIndex: 0,
                  transition: "all 0.5s ease",
                  pointerEvents: "none",
                }}
              />

              {/* Header: Icon + Title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  mb: 3,
                  zIndex: 1,
                }}
              >
                <Box
                  className="card-icon-container"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "56px",
                    height: "56px",
                    borderRadius: "16px",
                    background: "rgba(255, 174, 0, 0.08)",
                    border: "1px solid rgba(255, 174, 0, 0.25)",
                    color: "#FFAE00",
                    transition: "all 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: "28px" }} />
                </Box>

                <Typography
                  variant="h4"
                  fontWeight={900}
                  sx={{
                    color: "#FFAE00",
                    fontFamily: "Poppins",
                    fontSize: { xs: "1.45rem", md: "1.85rem" },
                  }}
                >
                  {t("ourVision")}
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: "#e0e0e0",
                  fontFamily: "Poppins",
                  lineHeight: 1.85,
                  textAlign: "justify",
                  fontSize: "1.05rem",
                  zIndex: 1,
                }}
              >
                {vision || ""}
              </Typography>
            </Paper>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default MissionVision;



